import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Expand } from "lucide-react";
import { GALLERY, GALLERY_CATEGORIES } from "@/data/media";
import { Seo } from "@/components/site/Seo";
import { Breadcrumbs, Container, Eyebrow, Img, MonogramPlaceholder, Reveal, WhatsAppButton } from "@/components/site/Primitives";
import { FinalCta } from "@/components/site/Blocks";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { quoteLink, track } from "@/lib/site";
import { cn } from "@/lib/utils";

export default function Gallery() {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const items = useMemo(() => (filter === "all" ? GALLERY : GALLERY.filter((g) => g.category === filter)), [filter]);
  const counts = useMemo(() => Object.fromEntries(GALLERY_CATEGORIES.map((c) => [c.value, c.value === "all" ? GALLERY.length : GALLERY.filter((g) => g.category === c.value).length])), []);

  return (
    <>
      <Seo title="Our Work | Printing, Branding & Custom Products Gallery | KhozaLand" description="View genuine KhozaLand work: personalised Ingcawe blankets, custom printed golf shirts, branded event mugs, personalised cushions and candles made in Cape Town." path="/gallery" />
      <section className="bg-white">
        <Container className="pt-6"><Breadcrumbs items={[{ name: "Our Work", path: "/gallery" }]} /></Container>
        <Container className="pb-10 pt-8 lg:pb-14 lg:pt-12">
          <Eyebrow>Gallery · Our work</Eyebrow>
          <h1 className="kl-h1 mt-5 max-w-4xl">Our Printing, Branding & Custom Work</h1>
          <p className="mt-6 max-w-2xl text-base leading-7 text-black/70 sm:text-lg">Every photograph below shows a real product made by KhozaLand. See something you like? Request something similar and make it your own.</p>
        </Container>
      </section>

      <div className="sticky top-[68px] z-30 border-y border-black/10 bg-white lg:top-[76px]">
        <Container>
          <div role="tablist" aria-label="Filter gallery by category" className="-mx-1 flex gap-2 overflow-x-auto py-3 [scrollbar-width:none] lg:flex-wrap" data-testid="gallery-category-tabs">
            {GALLERY_CATEGORIES.map((c) => (
              <button
                key={c.value}
                role="tab"
                aria-selected={filter === c.value}
                onClick={() => setFilter(c.value)}
                className={cn("shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors", filter === c.value ? "border-[#161616] bg-[#161616] text-white" : "border-black/15 bg-white text-black/70 hover:border-black/40")}
                data-testid={`gallery-filter-${c.value}`}
              >
                {c.label} <span className={filter === c.value ? "text-white/60" : "text-black/40"}>({counts[c.value]})</span>
              </button>
            ))}
          </div>
        </Container>
      </div>

      <section className="bg-[#F7F7F7] py-12 lg:py-16" aria-live="polite">
        <Container>
          {items.length ? (
            <ul className="columns-1 gap-4 sm:columns-2 lg:columns-3" data-testid="gallery-grid">
              {items.map((g, i) => (
                <Reveal as="li" key={g.id} delay={(i % 3) * 60} className="mb-4 break-inside-avoid">
                  <article className="kl-card group overflow-hidden" data-testid={`gallery-item-${g.id}`}>
                    <button type="button" onClick={() => setActive(g)} className="relative block w-full overflow-hidden bg-white text-left" style={{ aspectRatio: g.media.ratio }} aria-label={`View larger: ${g.title}`} data-testid={`gallery-item-${g.id}-open`}>
                      <Img media={g.media} sizes="(min-width:1024px) 32vw, (min-width:640px) 48vw, 92vw" className="kl-photo transition-transform duration-500 group-hover:scale-[1.03]" />
                      <span className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white text-[#161616] shadow"><Expand className="h-4 w-4" aria-hidden="true" /></span>
                    </button>
                    <div className="p-5">
                      <p className="kl-eyebrow text-black/50">{g.categoryLabel} · {g.type}</p>
                      <h2 className="mt-2 text-lg font-semibold tracking-tight">{g.title}</h2>
                      <p className="mt-1.5 text-sm leading-6 text-black/65">{g.description}</p>
                      <Link to={quoteLink(g.enquiry)} onClick={() => track("gallery_request_similar", { label: g.id })} className="mt-4 inline-flex text-sm font-semibold underline underline-offset-4" data-testid={`gallery-item-${g.id}-request-similar`}>Request Something Similar</Link>
                    </div>
                  </article>
                </Reveal>
              ))}
            </ul>
          ) : (
            <div className="mx-auto max-w-md text-center" data-testid="gallery-empty-state">
              <MonogramPlaceholder ratio="16/10" label="Photos coming soon" />
              <p className="mt-5 text-sm leading-6 text-black/65">We're adding photographs of this category soon. In the meantime, ask us about your project.</p>
              <Link to={quoteLink({})} className="kl-btn kl-btn-dark mt-5" data-testid="gallery-empty-quote-button">Request a Quote</Link>
            </div>
          )}
        </Container>
      </section>

      <Dialog open={Boolean(active)} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[92vh] w-[94vw] max-w-5xl overflow-y-auto border-black/10 bg-white p-0 sm:rounded-[18px]" data-testid="gallery-lightbox">
          {active ? (
            <div className="grid lg:grid-cols-5">
              <div className="bg-[#F7F7F7] p-3 lg:col-span-3">
                <img src={active.media.large} alt={active.media.alt} className="mx-auto max-h-[70vh] w-auto rounded-[12px] object-contain" />
              </div>
              <div className="flex flex-col p-6 lg:col-span-2 lg:p-8">
                <p className="kl-eyebrow text-black/50">{active.categoryLabel}</p>
                <DialogTitle className="kl-h3 mt-3">{active.title}</DialogTitle>
                <DialogDescription className="mt-3 text-[15px] leading-7 text-black/70">{active.description}</DialogDescription>
                <p className="mt-4 inline-flex w-fit rounded-full border border-black/15 px-3 py-1 text-xs font-medium">{active.type} · genuine KhozaLand photo</p>
                <div className="mt-auto flex flex-col gap-2 pt-8">
                  <Link to={quoteLink(active.enquiry)} onClick={() => track("gallery_request_similar", { label: active.id })} className="kl-btn kl-btn-dark h-12" data-testid="request-similar-button">Request Something Similar</Link>
                  <WhatsAppButton messageKey={active.whatsapp} className="h-12" testId="lightbox-whatsapp-button" />
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <FinalCta title="Have an idea of your own?" text="Share your design, logo or inspiration and KhozaLand will prepare a personalised quotation." />
    </>
  );
}
